'use client'

import { Database, Eye, EyeOff, Link2, Link2Off, Lock, Plus, Sparkles, Trash2 } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { Button } from './button'
import { Checkbox } from './checkbox'
import { Input } from './input'
import { Label } from './label'
import { OnboardingTargetPicker, type OnboardingTargetPickerItem } from './onboarding-target-picker'
import type { OnboardingFormField, OnboardingFormFieldOption, OnboardingFormFieldType } from './onboarding-task-form'
import { ONBOARDING_FIELD_TYPE_LABELS as FIELD_TYPE_LABELS, OnboardingTaskForm } from './onboarding-task-form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'
import { TagInput } from './tag-input'
import { Textarea } from './textarea'

/** Types a Customers user can add to a custom task. `order_group` is built-in only. */
const BUILDER_ONLY_EXCLUDED: readonly OnboardingFormFieldType[] = ['order_group']
const FIELD_TYPES = (Object.keys(FIELD_TYPE_LABELS) as OnboardingFormFieldType[]).filter(
  (type) => !BUILDER_ONLY_EXCLUDED.includes(type),
)

function isSelectType(type: OnboardingFormFieldType): boolean {
  return type === 'single_select' || type === 'multi_select'
}

/** Convert a human label into a stable camelCase identifier. Falls back to `field{n}`. */
export function slugifyFieldId(label: string, index: number): string {
  const words = label
    .trim()
    .replace(/[^a-zA-Z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
  const [first = '', ...rest] = words
  const camel = [
    first.toLowerCase(),
    ...rest.map((word) => word.slice(0, 1).toUpperCase() + word.slice(1).toLowerCase()),
  ].join('')
  return /^[a-zA-Z]/.test(camel) ? camel : `field${index + 1}`
}

/** Build `{ id, label }` table columns from free-text labels, guaranteeing unique ids. */
function columnsFromLabels(labels: string[]): NonNullable<OnboardingFormField['columns']> {
  return optionsFromLabels(labels).map((option) => ({ id: option.value, label: option.label }))
}

/** Build `{ value, label }` options from free-text labels, guaranteeing unique values. */
function optionsFromLabels(labels: string[]): OnboardingFormFieldOption[] {
  const used = new Set<string>()
  return labels.map((label, index) => {
    let value = slugifyFieldId(label, index)
    let suffix = 2
    while (used.has(value)) {
      value = `${slugifyFieldId(label, index)}${suffix}`
      suffix += 1
    }
    used.add(value)
    return { value, label }
  })
}

/**
 * Ensure every field id is unique within the form (later duplicates get a
 * numeric suffix). An existing id is preferred over the label so a field's id
 * stays STABLE once assigned — editing the label won't churn the id (which
 * keeps stored responses + provisioning mappings keyed to it valid).
 */
function withUniqueIds(fields: OnboardingFormField[]): OnboardingFormField[] {
  const used = new Set<string>()
  return fields.map((field, index) => {
    const base = slugifyFieldId(field.id || field.label || '', index)
    let id = base
    let suffix = 2
    while (used.has(id)) {
      id = `${base}${suffix}`
      suffix += 1
    }
    used.add(id)
    return { ...field, id }
  })
}

/** How a mapped answer becomes the destination value (mirrors @ordo/schema). */
export type OnboardingMappingExtract =
  | { kind: 'direct' }
  | { kind: 'coerce'; name: string }
  | { kind: 'ai'; instruction: string }

/** Marker that a select field's options are synced from its destination. */
export type OnboardingMappingOptionsSync = { kind: 'static'; syncedAt: string } | { kind: 'lookup'; source: string }

/** A field's optional binding to a destination in the onboarding target registry. */
export interface OnboardingFieldMapping {
  fieldId: string
  target: string
  extract?: OnboardingMappingExtract
  applyPolicy?: 'fillIfEmpty' | 'overwrite'
  optionsSync?: OnboardingMappingOptionsSync
}

/**
 * A destination the author can bind a field to — the client-safe view of a
 * registry entry (served by the customers /onboarding-targets endpoint).
 */
export interface OnboardingProvisionTarget extends OnboardingTargetPickerItem {
  valueType?: string
  options?: { kind: 'static'; values: ReadonlyArray<OnboardingFormFieldOption> } | { kind: 'lookup'; source: string }
}

/**
 * Plan the field patch + next mapping for a destination pick. Handles the
 * convert-action (picker chose an incompatible target → switch the question's
 * type) and dropdown preload (a select field adopting a destination that owns
 * its option set gets those options immediately — but hand-authored options
 * are never clobbered; the explicit "Sync options" button stays the override
 * for that case). Pure — exported for tests.
 */
export function planTargetSelection(input: {
  field: OnboardingFormField
  mapping: OnboardingFieldMapping | null
  target: string
  targetDef: OnboardingProvisionTarget | null
  convertTo?: OnboardingFormFieldType
  canPatchField: boolean
}): { fieldPatch: Partial<OnboardingFormField> | null; mapping: OnboardingFieldMapping } {
  const { field, mapping, target, targetDef, convertTo, canPatchField } = input
  const nextType = convertTo ?? field.type
  const staticOptions = targetDef?.options?.kind === 'static' ? targetDef.options.values : null
  const preload =
    isSelectType(nextType) &&
    staticOptions !== null &&
    canPatchField &&
    (convertTo !== undefined || (field.options ?? []).length === 0 || mapping?.optionsSync?.kind === 'static')
  let fieldPatch: Partial<OnboardingFormField> | null = null
  if (canPatchField && (convertTo || preload)) {
    fieldPatch = {
      ...(convertTo ? { type: nextType, options: isSelectType(nextType) ? (field.options ?? []) : undefined } : {}),
      ...(preload && staticOptions ? { options: staticOptions.map((option) => ({ ...option })) } : {}),
    }
  }
  // A new destination invalidates extract/optionsSync; lookup-backed select
  // fields auto-stamp the lookup marker (options resolve per school at render).
  const lookup = targetDef?.options?.kind === 'lookup' && isSelectType(nextType) ? targetDef.options.source : null
  return {
    fieldPatch,
    mapping: {
      fieldId: field.id,
      target,
      ...(lookup ? { optionsSync: { kind: 'lookup', source: lookup } } : {}),
      ...(preload ? { optionsSync: { kind: 'static', syncedAt: new Date().toISOString() } } : {}),
    },
  }
}

/** Result of the host's Ask-AI test bench call. */
export type OnboardingExtractionTestResult =
  | { outcome: 'value'; value: unknown }
  | { outcome: 'abstain'; reason: string | null }

export interface OnboardingTaskFormBuilderProps {
  fields: OnboardingFormField[]
  onChange: (fields: OnboardingFormField[]) => void
  className?: string
  /** When provided, each field shows a compact "Provisions →" selector. */
  provisionTargets?: readonly OnboardingProvisionTarget[]
  mappings?: OnboardingFieldMapping[]
  onMappingsChange?: (mappings: OnboardingFieldMapping[]) => void
  /**
   * Ask-AI test bench: run a mapping's extraction against a pasted sample
   * answer. Absent → the test affordance is hidden (mode still authorable).
   */
  onTestExtraction?: (input: {
    target: string
    instruction: string
    question: string
    sampleAnswer: string
  }) => Promise<OnboardingExtractionTestResult>
}

/**
 * Builder counterpart to `OnboardingTaskForm`. Lets an internal operator define
 * the fields a school must fill out for an onboarding task, emitting the same
 * `OnboardingFormField[]` shape the renderer consumes. Owns id + option-value
 * generation so its output is directly persistable.
 */
export function OnboardingTaskFormBuilder({
  fields,
  onChange,
  className,
  provisionTargets,
  mappings,
  onMappingsChange,
  onTestExtraction,
}: OnboardingTaskFormBuilderProps) {
  const [showPreview, setShowPreview] = React.useState(false)

  function update(nextFields: OnboardingFormField[]) {
    const unique = withUniqueIds(nextFields)
    onChange(unique)
    // Drop mappings whose field was removed (ids are otherwise stable).
    if (onMappingsChange && mappings) {
      const ids = new Set(unique.map((field) => field.id))
      const pruned = mappings.filter((mapping) => ids.has(mapping.fieldId))
      if (pruned.length !== mappings.length) {
        onMappingsChange(pruned)
      }
    }
  }

  function updateField(index: number, patch: Partial<OnboardingFormField>) {
    update(fields.map((field, i) => (i === index ? { ...field, ...patch } : field)))
  }

  function addField() {
    update([...fields, { id: '', label: '', type: 'text', required: false }])
  }

  function removeField(index: number) {
    update(fields.filter((_, i) => i !== index))
  }

  /** Replace or clear a field's mapping, keeping each target bound at most once. */
  function setFieldMapping(fieldId: string, next: OnboardingFieldMapping | null) {
    if (!onMappingsChange) {
      return
    }
    const current = mappings ?? []
    if (!next) {
      onMappingsChange(current.filter((mapping) => mapping.fieldId !== fieldId))
      return
    }
    const base = current.filter((mapping) => mapping.fieldId !== fieldId && mapping.target !== next.target)
    onMappingsChange([...base, next])
  }

  function mappingFor(fieldId: string): OnboardingFieldMapping | null {
    return mappings?.find((mapping) => mapping.fieldId === fieldId) ?? null
  }

  /** target value → label of the field it's mapped to (for the picker's "already mapped" rows). */
  function mappedTargetLabels(exceptFieldId: string): ReadonlyMap<string, string> {
    const labels = new Map<string, string>()
    for (const mapping of mappings ?? []) {
      if (mapping.fieldId === exceptFieldId) {
        continue
      }
      const field = fields.find((candidate) => candidate.id === mapping.fieldId)
      labels.set(mapping.target, field?.label || mapping.fieldId)
    }
    return labels
  }

  const previewFields = fields.filter((field) => field.label.trim().length > 0)

  return (
    <div className={cn('space-y-4', className)}>
      {fields.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No fields yet. Add fields the school will fill out, or leave empty for a task they complete manually.
        </p>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) =>
            BUILDER_ONLY_EXCLUDED.includes(field.type) ? (
              // Built-in field types (e.g. order_group) can't be authored here,
              // but must round-trip when editing a template that already has
              // one. Show a locked, read-only row instead of a broken editor.
              <LockedFieldRow
                key={index}
                field={field}
                provisionTargets={provisionTargets}
                mapping={mappingFor(field.id)}
                mappedTargets={mappedTargetLabels(field.id)}
                onMappingChange={onMappingsChange ? (next) => setFieldMapping(field.id, next) : undefined}
                onTestExtraction={onTestExtraction}
              />
            ) : (
              <FieldRow
                key={index}
                field={field}
                canRemove={fields.length > 0}
                onChange={(patch) => updateField(index, patch)}
                onRemove={() => removeField(index)}
                provisionTargets={provisionTargets}
                mapping={mappingFor(field.id)}
                mappedTargets={mappedTargetLabels(field.id)}
                onMappingChange={onMappingsChange ? (next) => setFieldMapping(field.id, next) : undefined}
                onTestExtraction={onTestExtraction}
              />
            ),
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="outline" size="sm" onClick={addField}>
          <Plus className="h-4 w-4" />
          Add field
        </Button>
        {previewFields.length > 0 ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowPreview((current) => !current)}>
            {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPreview ? 'Hide preview' : 'Preview'}
          </Button>
        ) : null}
      </div>

      {showPreview && previewFields.length > 0 ? (
        <div className="space-y-2 rounded-md border bg-muted/30 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">School preview</p>
          <OnboardingTaskForm fields={previewFields} readOnly hideActions />
        </div>
      ) : null}
    </div>
  )
}

/**
 * "Feeds →" destination controls for a field: the searchable target picker,
 * plus (once mapped) the apply-policy toggle and — for select fields whose
 * destination owns its allowed values — the options-sync affordance. Shown
 * only when the host passes a target catalog.
 */
/** Field types whose values have no meaningful free-text for AI extraction. */
const AI_EXCLUDED_FIELD_TYPES: readonly OnboardingFormFieldType[] = [
  'document_upload',
  'order_group',
  'address',
  'rows',
]

function MappingControls({
  field,
  provisionTargets,
  mapping,
  mappedTargets,
  onMappingChange,
  onFieldPatch,
  onTestExtraction,
}: {
  field: OnboardingFormField
  provisionTargets?: readonly OnboardingProvisionTarget[]
  mapping: OnboardingFieldMapping | null
  mappedTargets: ReadonlyMap<string, string>
  onMappingChange?: (mapping: OnboardingFieldMapping | null) => void
  onFieldPatch?: (patch: Partial<OnboardingFormField>) => void
  onTestExtraction?: OnboardingTaskFormBuilderProps['onTestExtraction']
}) {
  if (!provisionTargets || !onMappingChange) {
    return null
  }
  const targetDef = mapping ? (provisionTargets.find((target) => target.value === mapping.target) ?? null) : null
  const staticOptions = targetDef?.options?.kind === 'static' ? targetDef.options.values : null
  const lookupSource = targetDef?.options?.kind === 'lookup' ? targetDef.options.source : null
  const canSyncOptions = isSelectType(field.type) && staticOptions !== null
  const synced = mapping?.optionsSync !== undefined

  function handleTargetChange(target: string | null, selectOptions?: { convertTo?: string }) {
    if (!target) {
      onMappingChange!(null)
      return
    }
    const planned = planTargetSelection({
      field,
      mapping,
      target,
      targetDef: provisionTargets!.find((candidate) => candidate.value === target) ?? null,
      convertTo: selectOptions?.convertTo as OnboardingFormFieldType | undefined,
      canPatchField: onFieldPatch !== undefined,
    })
    if (planned.fieldPatch && onFieldPatch) {
      onFieldPatch(planned.fieldPatch)
    }
    onMappingChange!(planned.mapping)
  }

  function handleSyncOptions() {
    if (!mapping || !staticOptions || !onFieldPatch) {
      return
    }
    onFieldPatch({ options: staticOptions.map((option) => ({ ...option })) })
    onMappingChange!({ ...mapping, optionsSync: { kind: 'static', syncedAt: new Date().toISOString() } })
  }

  function handleDetachOptions() {
    if (!mapping) {
      return
    }
    const { optionsSync: _optionsSync, ...rest } = mapping
    onMappingChange!(rest)
  }

  const aiCapable = !AI_EXCLUDED_FIELD_TYPES.includes(field.type)
  const aiMode = mapping?.extract?.kind === 'ai'

  function handleModeChange(mode: string) {
    if (!mapping) {
      return
    }
    if (mode === 'ai') {
      onMappingChange!({
        ...mapping,
        extract: { kind: 'ai', instruction: mapping.extract?.kind === 'ai' ? mapping.extract.instruction : '' },
      })
    } else {
      const { extract: _extract, ...rest } = mapping
      onMappingChange!(rest)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Feeds</Label>
          <OnboardingTargetPicker
            targets={provisionTargets}
            fieldType={field.type}
            fieldLabel={field.label}
            value={mapping?.target ?? null}
            onChange={handleTargetChange}
            // Locked rows (built-in fields) can't have their type switched.
            convertibleFieldTypes={onFieldPatch ? FIELD_TYPES : undefined}
            mappedTargets={mappedTargets}
            className="w-64"
          />
        </div>
        {mapping ? (
          <Select
            value={mapping.applyPolicy ?? 'fillIfEmpty'}
            onValueChange={(value) =>
              onMappingChange({ ...mapping, applyPolicy: value === 'overwrite' ? 'overwrite' : 'fillIfEmpty' })
            }>
            <SelectTrigger className="h-9 w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fillIfEmpty">Only fill if empty</SelectItem>
              <SelectItem value="overwrite">Overwrite existing</SelectItem>
            </SelectContent>
          </Select>
        ) : null}
        {mapping && aiCapable ? (
          <Select value={aiMode ? 'ai' : 'direct'} onValueChange={handleModeChange}>
            <SelectTrigger className="h-9 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="direct">Use answer as-is</SelectItem>
              <SelectItem value="ai">Ask AI</SelectItem>
            </SelectContent>
          </Select>
        ) : null}
      </div>
      {mapping && aiMode && mapping.extract?.kind === 'ai' ? (
        <AskAiControls
          field={field}
          targetDef={targetDef}
          instruction={mapping.extract.instruction}
          onInstructionChange={(instruction) => onMappingChange({ ...mapping, extract: { kind: 'ai', instruction } })}
          onTestExtraction={onTestExtraction}
        />
      ) : null}
      {mapping && ((lookupSource && isSelectType(field.type)) || canSyncOptions) ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {lookupSource && isSelectType(field.type) ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
              <Database className="h-3 w-3" aria-hidden />
              Options looked up per school
            </span>
          ) : null}
          {canSyncOptions ? (
            synced ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                <Link2 className="h-3 w-3" aria-hidden />
                Options synced from destination
                <button
                  type="button"
                  onClick={handleDetachOptions}
                  className="ml-0.5 inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline"
                  aria-label="Detach options from destination">
                  <Link2Off className="h-3 w-3" aria-hidden />
                  Detach
                </button>
              </span>
            ) : (
              <Button type="button" variant="ghost" size="sm" onClick={handleSyncOptions} disabled={!onFieldPatch}>
                <Link2 className="h-4 w-4" />
                Sync options from destination
              </Button>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Ask-AI extraction editor: the author's instruction, the destination's
 * locked allowed outputs, and (when the host wires `onTestExtraction`) a
 * sample-answer test bench — untested instructions are where extractions go
 * wrong quietly.
 */
function AskAiControls({
  field,
  targetDef,
  instruction,
  onInstructionChange,
  onTestExtraction,
}: {
  field: OnboardingFormField
  targetDef: OnboardingProvisionTarget | null
  instruction: string
  onInstructionChange: (instruction: string) => void
  onTestExtraction?: OnboardingTaskFormBuilderProps['onTestExtraction']
}) {
  const [sample, setSample] = React.useState('')
  const [testing, setTesting] = React.useState(false)
  const [result, setResult] = React.useState<OnboardingExtractionTestResult | null>(null)
  const staticOptions = targetDef?.options?.kind === 'static' ? targetDef.options.values : null

  async function runTest() {
    if (!onTestExtraction || !targetDef || !instruction.trim() || !sample.trim()) {
      return
    }
    setTesting(true)
    setResult(null)
    try {
      setResult(
        await onTestExtraction({
          target: targetDef.value,
          instruction: instruction.trim(),
          question: field.label,
          sampleAnswer: sample.trim(),
        }),
      )
    } catch {
      setResult({ outcome: 'abstain', reason: 'Test failed — try again.' })
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
      <div className="space-y-1.5">
        <Label className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <Sparkles className="h-3 w-3" aria-hidden />
          What should AI figure out from the answer?
        </Label>
        <Textarea
          value={instruction}
          placeholder={`e.g. From the answer, determine the ${targetDef?.label ?? 'destination value'}.`}
          onChange={(event) => onInstructionChange(event.target.value)}
          rows={2}
        />
      </div>
      {staticOptions ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Allowed outputs</span>
          {staticOptions.map((option) => (
            <span
              key={option.value}
              className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" aria-hidden />
              {option.label}
            </span>
          ))}
          <span className="rounded-full border border-dashed px-2.5 py-0.5 text-xs text-muted-foreground">
            Can't determine → writes nothing
          </span>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          If AI can't determine a value from the answer, nothing is written — the raw answer stays on the task.
        </p>
      )}
      {onTestExtraction ? (
        <div className="space-y-2">
          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Test with a sample answer
          </Label>
          <Textarea
            value={sample}
            placeholder="Paste a realistic answer a school admin might give…"
            onChange={(event) => setSample(event.target.value)}
            rows={2}
          />
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={testing || !instruction.trim() || !sample.trim()}
              onClick={() => void runTest()}>
              {testing ? 'Testing…' : 'Run test'}
            </Button>
            {result ? (
              result.outcome === 'value' ? (
                <span className="text-sm">
                  <span className="font-medium">Extracted:</span>{' '}
                  {typeof result.value === 'string' ? result.value : JSON.stringify(result.value)}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">
                  Abstained{result.reason ? ` — ${result.reason}` : ''}
                </span>
              )
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

/** Read-only row for a built-in field type the builder can't author (e.g. order_group). */
function LockedFieldRow({
  field,
  provisionTargets,
  mapping,
  mappedTargets,
  onMappingChange,
  onTestExtraction,
}: {
  field: OnboardingFormField
  provisionTargets?: readonly OnboardingProvisionTarget[]
  mapping: OnboardingFieldMapping | null
  mappedTargets: ReadonlyMap<string, string>
  onMappingChange?: (mapping: OnboardingFieldMapping | null) => void
  onTestExtraction?: OnboardingTaskFormBuilderProps['onTestExtraction']
}) {
  return (
    <div className="space-y-3 rounded-xl border border-dashed bg-muted/30 p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{field.label || FIELD_TYPE_LABELS[field.type]}</p>
          <p className="text-xs text-muted-foreground">
            {FIELD_TYPE_LABELS[field.type]} · built-in field, managed by Ordo
          </p>
        </div>
        <Lock className="h-4 w-4 shrink-0 text-muted-foreground/60" aria-hidden />
      </div>
      <MappingControls
        field={field}
        provisionTargets={provisionTargets}
        mapping={mapping}
        mappedTargets={mappedTargets}
        onMappingChange={onMappingChange}
        onTestExtraction={onTestExtraction}
      />
    </div>
  )
}

function FieldRow({
  field,
  canRemove,
  onChange,
  onRemove,
  provisionTargets,
  mapping,
  mappedTargets,
  onMappingChange,
  onTestExtraction,
}: {
  field: OnboardingFormField
  canRemove: boolean
  onChange: (patch: Partial<OnboardingFormField>) => void
  onRemove: () => void
  provisionTargets?: readonly OnboardingProvisionTarget[]
  mapping: OnboardingFieldMapping | null
  mappedTargets: ReadonlyMap<string, string>
  onMappingChange?: (mapping: OnboardingFieldMapping | null) => void
  onTestExtraction?: OnboardingTaskFormBuilderProps['onTestExtraction']
}) {
  const optionLabels = (field.options ?? []).map((option) => option.label)

  return (
    <div className="group rounded-xl border bg-background p-4 transition-colors hover:border-foreground/20 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1 space-y-4">
          {/* The field label is the question itself — give it room. */}
          <Input
            value={field.label}
            placeholder="e.g. First date of meal service"
            onChange={(event) => onChange({ label: event.target.value })}
          />

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-2">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Type</Label>
              <Select
                value={field.type}
                onValueChange={(value) => {
                  const type = value as OnboardingFormFieldType
                  onChange({
                    type,
                    options: isSelectType(type) ? (field.options ?? []) : undefined,
                    columns: type === 'rows' ? (field.columns ?? []) : undefined,
                  })
                }}>
                <SelectTrigger className="h-9 w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {FIELD_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={field.required === true}
                onCheckedChange={(checked) => onChange({ required: Boolean(checked) })}
              />
              Required
            </label>
          </div>

          <MappingControls
            field={field}
            provisionTargets={provisionTargets}
            mapping={mapping}
            mappedTargets={mappedTargets}
            onMappingChange={onMappingChange}
            onFieldPatch={onChange}
            onTestExtraction={onTestExtraction}
          />

          {field.type === 'rows' ? (
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Columns</Label>
              <TagInput
                value={(field.columns ?? []).map((column) => column.label)}
                placeholder="Type a column (e.g. Classroom) and press Enter"
                onChange={(labels) => onChange({ columns: columnsFromLabels(labels) })}
              />
              <p className="text-xs text-muted-foreground">
                The school enters one row per section — e.g. columns "Classroom" and "Lunch period".
              </p>
            </div>
          ) : null}

          {isSelectType(field.type) ? (
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Options</Label>
              {mapping?.optionsSync?.kind === 'lookup' ? (
                <p className="text-sm text-muted-foreground">
                  Options come from each school's own configuration when they open the task.
                </p>
              ) : (
                <TagInput
                  value={optionLabels}
                  placeholder="Type an option and press Enter"
                  disabled={mapping?.optionsSync?.kind === 'static'}
                  onChange={(labels) => onChange({ options: optionsFromLabels(labels) })}
                />
              )}
            </div>
          ) : null}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={!canRemove}
          aria-label="Remove field"
          onClick={onRemove}
          className="-mr-1 shrink-0 text-muted-foreground/50 transition-colors hover:bg-destructive/10 hover:text-destructive">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
